import { useEffect, useState } from 'react';
import { Modal } from 'antd';
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, Check, X, Inbox } from 'lucide-react';
import { api } from '../api';
import { SkeletonTable } from '../components/Skeleton';

type LogRow = {
  id: number;
  received_at: string;
  ip: string | null;
  user_agent: string | null;
  url_path: string;
  token_path: string;
  token_match: number;
  raw_body: string | null;
  parsed_ok: number;
  ord_num: string | null;
  mcht_id: string | null;
  is_test: number;
  result_cd: string;
  result_msg: string;
  http_status: number;
  status: 'registered' | 'cancelled' | 'duplicate' | 'auth_failed' | 'invalid_json' | 'validation_failed' | 'server_error';
  error_detail: string | null;
  duration_ms: number | null;
  reprocessed_at: string | null;
  reprocess_outcome: string | null;
  reprocess_error: string | null;
  trx_dttm: string | null;
  settlmnt_cycle: string | null;
  noti_kind: 'merchant' | 'merchant_discount' | null;
};

type Summary = {
  total: number;
  op_total: number;
  test_total: number;
  today_kst: number;
  last_24h: number;
  by_status: Array<{ status: string; n: number }>;
};

const STATUS_KO: Record<string, string> = {
  registered: '저장 성공',
  cancelled: '취소 반영',
  duplicate: '중복 (이미 기록된 거래)',
  auth_failed: '인증정보 확인 실패',
  invalid_json: '수신 데이터 형식 오류',
  validation_failed: '수신정보 검증 실패',
  server_error: '처리 중 오류',
};

const NOTI_KIND_KO: Record<string, string> = {
  merchant: '일반 결제 수신',
  merchant_discount: '할인매매 결제 수신',
};
const NOTI_KIND_CLASS: Record<string, string> = {
  merchant: 'merchant',
  merchant_discount: 'merchant-discount',
};

function todayKstStr(): string {
  const now = new Date();
  const kst = new Date(now.getTime() + 9 * 3600 * 1000);
  return kst.toISOString().slice(0, 10);
}
function shiftDays(d: string, delta: number): string {
  const dt = new Date(d + 'T00:00:00Z');
  dt.setUTCDate(dt.getUTCDate() + delta);
  return dt.toISOString().slice(0, 10);
}

export default function NotificationsLog() {
  const [from, setFrom] = useState(todayKstStr());
  const [to, setTo] = useState(todayKstStr());
  const [trxFrom, setTrxFrom] = useState('');
  const [trxTo, setTrxTo] = useState('');
  const [status, setStatus] = useState<'' | LogRow['status']>('');
  const [ordNum, setOrdNum] = useState('');
  const [mchtId, setMchtId] = useState('');
  const [tokenMatch, setTokenMatch] = useState<'' | '0' | '1'>('');
  const [origin, setOrigin] = useState<'' | 'op' | 'test'>('op');
  const [notiKind, setNotiKind] = useState<'' | 'merchant' | 'merchant_discount'>('');
  const [settlmntCycle, setSettlmntCycle] = useState('');
  const [kw, setKw] = useState('');
  const [httpStatus, setHttpStatus] = useState('');
  const [parsedOk, setParsedOk] = useState<'' | '0' | '1'>('');
  const [reprocessOutcome, setReprocessOutcome] = useState<'' | LogRow['status']>('');
  const [resultCd, setResultCd] = useState('');
  const [ip, setIp] = useState('');
  const [durationMin, setDurationMin] = useState('');
  const [durationMax, setDurationMax] = useState('');
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);

  const [data, setData] = useState<LogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [detail, setDetail] = useState<LogRow | null>(null);
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessMsg, setReprocessMsg] = useState<string | null>(null);

  const loadSummary = async () => {
    try { setSummary(await api<Summary>('GET', '/notifications/log/summary')); }
    catch {}
  };
  useEffect(() => { loadSummary(); }, []);

  useEffect(() => {
    let active = true;
    setErr(null); setLoading(true);
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (trxFrom) params.set('trx_from', trxFrom);
    if (trxTo) params.set('trx_to', trxTo);
    if (status) params.set('status', status);
    if (ordNum.trim()) params.set('ord_num', ordNum.trim());
    if (mchtId.trim()) params.set('mcht_id', mchtId.trim());
    if (tokenMatch !== '') params.set('token_match', tokenMatch);
    if (origin) params.set('origin', origin);
    if (notiKind) params.set('noti_kind', notiKind);
    if (settlmntCycle.trim()) params.set('settlmnt_cycle', settlmntCycle.trim());
    if (kw.trim()) params.set('q', kw.trim());
    if (httpStatus.trim()) params.set('http_status', httpStatus.trim());
    if (parsedOk !== '') params.set('parsed_ok', parsedOk);
    if (reprocessOutcome) params.set('reprocess_outcome', reprocessOutcome);
    if (resultCd.trim()) params.set('result_cd', resultCd.trim());
    if (ip.trim()) params.set('ip', ip.trim());
    if (durationMin.trim()) params.set('duration_min', durationMin.trim());
    if (durationMax.trim()) params.set('duration_max', durationMax.trim());
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    (async () => {
      try {
        const r = await api<{ total: number; data: LogRow[] }>('GET', `/notifications/log?${params}`);
        if (!active) return;
        setData(r.data ?? []);
        setTotal(r.total ?? 0);
      } catch (e: any) {
        if (active) setErr(e?.message ?? '조회 실패');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [
    from, to, trxFrom, trxTo, status, ordNum, mchtId, tokenMatch, origin,
    notiKind, settlmntCycle,
    kw, httpStatus, parsedOk, reprocessOutcome, resultCd, ip, durationMin, durationMax,
    limit, offset,
  ]);

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const curPage = Math.floor(offset / limit) + 1;
  const firstIdx = total === 0 ? 0 : offset + 1;
  const lastIdx = Math.min(offset + limit, total);
  const goPage = (p: number) => {
    const safe = Math.max(1, Math.min(totalPages, p));
    setOffset((safe - 1) * limit);
  };

  const onApply = (e: React.FormEvent) => { e.preventDefault(); setOffset(0); };

  const runReprocess = async () => {
    if (reprocessing) return;
    // 최신 요약 재조회 후 실패 유형별 건수로 최종 확인.
    let sum: Summary | null = summary;
    try { sum = await api<Summary>('GET', '/notifications/log/summary'); setSummary(sum); } catch {}
    const failStatuses = ['validation_failed', 'server_error'] as const;
    const perType = failStatuses.map(s => ({
      s, n: sum?.by_status.find(x => x.status === s)?.n ?? 0,
    }));
    const total = perType.reduce((a, b) => a + b.n, 0);
    if (total === 0) {
      Modal.info({
        title: '재처리 대상 없음',
        content: '현재 재처리 대상(검증 실패 + 서버 오류)이 없습니다.',
        okText: '확인',
      });
      return;
    }
    Modal.confirm({
      title: `실패 건 일괄 재처리 — 총 ${total.toLocaleString('ko-KR')}건`,
      width: 520,
      content: (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <div style={{ padding: '8px 10px', background: '#fef3c7', color: '#854d0e', borderRadius: 6, marginBottom: 10 }}>
            <b>재처리 범위</b>: 화면의 필터·기간과 무관하게 <b>전체 실패 건</b>이 대상입니다.
          </div>
          <div style={{ marginBottom: 6 }}><b>실패 유형별 건수</b></div>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            {perType.map(x => (
              <li key={x.s}>{STATUS_KO[x.s]}: <b>{x.n.toLocaleString('ko-KR')}건</b></li>
            ))}
          </ul>
        </div>
      ),
      okText: `${total.toLocaleString('ko-KR')}건 재처리 실행`,
      okType: 'danger',
      cancelText: '취소',
      onOk: async () => {
        setReprocessing(true);
        setReprocessMsg(`재처리 중 — 대상 ${total.toLocaleString('ko-KR')}건 처리 중`);
        try {
          let totalProc = 0, totalReg = 0, totalDup = 0, totalStill = 0;
          let safety = 50;
          while (safety-- > 0) {
            const r = await api<{
              candidates: number; processed: number; registered: number;
              duplicate: number; still_failed: number; remaining: number;
              failure_breakdown: Record<string, number>;
            }>('POST', '/notifications/log/reprocess', { limit: 500 });
            totalProc += r.processed;
            totalReg += r.registered;
            totalDup += r.duplicate;
            totalStill += r.still_failed;
            setReprocessMsg(`재처리 진행 중 — 누적 ${totalProc}건 / 신규저장 ${totalReg} / 중복 ${totalDup} / 여전히실패 ${totalStill} (남은 ${r.remaining}건)`);
            if (r.processed === 0 || r.remaining === 0) break;
          }
          setReprocessMsg(`재처리 완료 — 대상 ${total.toLocaleString('ko-KR')}건 · 신규 ${totalReg.toLocaleString('ko-KR')} · 중복 ${totalDup.toLocaleString('ko-KR')} · 재실패 ${totalStill.toLocaleString('ko-KR')}`);
          Modal.success({
            title: '재처리 완료',
            width: 480,
            content: (
              <div style={{ fontSize: 13, lineHeight: 1.7 }}>
                <div><b>대상:</b> {total.toLocaleString('ko-KR')}건</div>
                <ul style={{ paddingLeft: 18, margin: '6px 0 0' }}>
                  <li>신규 저장: <b>{totalReg.toLocaleString('ko-KR')}</b>건</li>
                  <li>중복(이미 처리됨): <b>{totalDup.toLocaleString('ko-KR')}</b>건</li>
                  <li>재실패: <b style={{ color: totalStill > 0 ? '#b91c1c' : undefined }}>{totalStill.toLocaleString('ko-KR')}</b>건</li>
                </ul>
                {totalStill > 0 && (
                  <div style={{ marginTop: 10, color: 'var(--text-muted)', fontSize: 12 }}>
                    재실패 건은 하단 목록의 "처리 결과" 필터를 <b>본문 검증 실패 / 서버 처리 오류</b>로 두고 확인하실 수 있습니다.
                  </div>
                )}
              </div>
            ),
            okText: '확인',
          });
          await loadSummary();
          await loadReprocessHistory();
          setOffset(0);
        } catch (e: any) {
          setReprocessMsg(`재처리 오류: ${e?.message ?? '알 수 없음'}`);
          Modal.error({ title: '재처리 오류', content: e?.message ?? '알 수 없음', okText: '확인' });
        } finally {
          setReprocessing(false);
        }
      },
    });
  };

  // 버튼 라벨용 실패 총 건수 (요약 캐시 기반)
  const failCount = ((summary?.by_status.find(x => x.status === 'validation_failed')?.n ?? 0)
                   + (summary?.by_status.find(x => x.status === 'server_error')?.n ?? 0));

  // 재처리 이력
  type ReprocessRun = {
    id: number; actor: string; started_at: string; finished_at: string | null;
    target_count: number; processed_count: number; registered_count: number;
    duplicate_count: number; failed_count: number; detail: string | null;
  };
  const [reprocessHistory, setReprocessHistory] = useState<ReprocessRun[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const loadReprocessHistory = async () => {
    try {
      const r = await api<{ data: ReprocessRun[] }>('GET', '/notifications/log/reprocess-history?limit=20');
      setReprocessHistory(r.data ?? []);
    } catch { /* best-effort */ }
  };
  useEffect(() => { loadReprocessHistory(); }, []);

  const resetAdvancedFilters = () => {
    setKw(''); setHttpStatus(''); setParsedOk(''); setReprocessOutcome('');
    setResultCd(''); setIp(''); setDurationMin(''); setDurationMax('');
    setOffset(0);
  };

  const statusN = (s: string): number => summary?.by_status.find(x => x.status === s)?.n ?? 0;

  return (
    <div>
      <header className="page-header">
        <div>
          <h2 className="page-title">결제 수신 로그</h2>
          <p className="page-sub">
            PG가 우리 콜백 주소로 보낸 모든 요청의 원본 본문과 처리 결과가 빠짐없이 기록됩니다. 인증 실패·JSON 깨짐·검증 실패도 모두 포함.<br />
            <b>테스트 발신은 정산에 절대 반영되지 않으며, 기본 화면에서도 숨겨집니다.</b> "발신 구분 → 테스트만/전체"로 바꾸면 노출.
          </p>
        </div>
        <div className="page-actions">
          <button type="button" onClick={loadSummary} className="filter-ghost">요약 새로고침</button>
          <button type="button" onClick={runReprocess} disabled={reprocessing} className="filter-warn-btn"
                  title="화면 필터와 무관하게 전체 실패 건(검증 실패 + 서버 오류)을 재처리합니다.">
            {reprocessing
              ? '재처리 중…'
              : failCount > 0
                ? `실패 건 일괄 재처리 (${failCount.toLocaleString('ko-KR')}건)`
                : '실패 건 일괄 재처리'}
          </button>
        </div>
      </header>

      <details className="card help-block">
        <summary>ⓘ 결제 수신 로그 안내 — 용어/처리 결과 설명</summary>
        <div className="help-body">
          <h4>이 화면이 보여주는 것</h4>
          <ul>
            <li>PG가 우리 결제 수신 주소로 보낸 <b>모든 요청</b>을 수신 원문 그대로 기록.</li>
            <li>저장 성공·취소 반영·중복·인증정보 확인 실패·수신 데이터 형식 오류·수신정보 검증 실패·처리 중 오류 등 모든 결과를 추적할 수 있습니다.</li>
            <li>기본 필터는 운영 발신(테스트 제외) + 최근 7일. 필요 시 필터에서 조정.</li>
          </ul>

          <h4>처리 결과 종류</h4>
          <ul>
            <li><b className="log-status registered">저장 성공</b>: 새 주문번호로 결제 원장에 정상 적재.</li>
            <li><b className="log-status cancelled">취소 반영</b>: 같은 주문번호의 기존 결제가 취소 상태로 갱신됨.</li>
            <li><b className="log-status duplicate">중복</b>: 같은 주문번호의 결제 수신이 또 왔거나, 이미 취소된 건의 재발사. 무시됨.</li>
            <li><b className="log-status auth_failed">인증정보 확인 실패</b>: 인증정보 검증 통과 못함 (현재 운영 라우트는 인증정보 없이 열려 있어 거의 발생 X).</li>
            <li><b className="log-status invalid_json">수신 데이터 형식 오류</b>: 수신 원문이 정상 형식이 아님.</li>
            <li><b className="log-status validation_failed">수신정보 검증 실패</b>: 필수 항목 누락 또는 값 형식 오류. <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>기술 상세: Zod 스키마 위반</span></li>
            <li><b className="log-status server_error">처리 중 오류</b>: 데이터 저장 중 오류가 발생. <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>기술 상세: 데이터 저장 실패 또는 처리 로직 예외</span></li>
          </ul>

          <h4>필터 — 수신일자 vs 결제일자</h4>
          <ul>
            <li><b>수신일자</b>: 우리 서버가 결제 수신 요청을 받은 시점 (KST). 어제 발생한 거래라도 오늘 PG가 재발사하면 수신일자=오늘.</li>
            <li><b>결제일자</b>: PG가 본문에 보낸 결제 발생시각에서 추출한 KST 일자. 거래 자체가 언제 일어났는지.</li>
            <li>두 필터는 독립적으로 조합 가능. 예: 결제는 4월인데 수신은 5월(지연 발사) → 결제일자 4월/수신일자 5월로 조회.</li>
          </ul>

          <h4>실패분 일괄 재처리 버튼</h4>
          <p style={{ margin: '4px 0 0 0' }}>
            오른쪽 노란 버튼 클릭 시, "수신정보 검증 실패" + "처리 중 오류" 상태인 행들의 수신 원문을 다시 처리해 재시도합니다.
            원래 처리 결과는 보존되고 "재처리 결과" 컬럼에 신규 처리 결과가 표시됩니다. 500건씩 자동 반복.
          </p>
        </div>
      </details>

      {/* 재처리 이력 */}
      <div className="card" style={{ padding: '10px 14px', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 13 }}>
            <b>재처리 이력</b>
            <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>
              최근 {reprocessHistory.length}건 · 최신: {reprocessHistory[0]
                ? `${reprocessHistory[0].started_at} · ${reprocessHistory[0].actor} · 대상 ${reprocessHistory[0].target_count.toLocaleString('ko-KR')}건`
                : '없음'}
            </span>
          </div>
          <button className="filter-ghost" style={{ fontSize: 12 }} onClick={() => setShowHistory(v => !v)}>
            {showHistory ? '숨기기' : '전체 보기'}
          </button>
        </div>
        {showHistory && reprocessHistory.length > 0 && (
          <div className="table-wrap" style={{ maxHeight: 320, marginTop: 10, border: 0 }}>
            <table>
              <thead><tr>
                <th className="nowrap">시작 (KST)</th>
                <th className="nowrap">종료 (KST)</th>
                <th className="nowrap">처리자</th>
                <th className="num nowrap">대상</th>
                <th className="num nowrap">신규</th>
                <th className="num nowrap">중복</th>
                <th className="num nowrap">재실패</th>
              </tr></thead>
              <tbody>
                {reprocessHistory.map(h => (
                  <tr key={h.id}>
                    <td className="nowrap" style={{ fontFamily: 'monospace', fontSize: 12 }}>{h.started_at}</td>
                    <td className="nowrap" style={{ fontFamily: 'monospace', fontSize: 12 }}>{h.finished_at ?? '-'}</td>
                    <td className="nowrap" style={{ fontFamily: 'monospace', fontSize: 12 }}>{h.actor}</td>
                    <td className="num nowrap">{h.target_count.toLocaleString('ko-KR')}</td>
                    <td className="num nowrap" style={{ color: 'var(--leaf-700)', fontWeight: 600 }}>{h.registered_count.toLocaleString('ko-KR')}</td>
                    <td className="num nowrap">{h.duplicate_count.toLocaleString('ko-KR')}</td>
                    <td className="num nowrap" style={{ color: h.failed_count > 0 ? '#b91c1c' : undefined, fontWeight: h.failed_count > 0 ? 700 : undefined }}>{h.failed_count.toLocaleString('ko-KR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {summary && (
        <>
          <div className="section-label">전체 수신 (운영 + 테스트) · <span style={{ color: 'var(--text-muted)', fontSize: 11, fontWeight: 400 }}>카드 클릭 시 하단 필터 자동 적용</span></div>
          <div className="summary" style={{ marginBottom: 10 }}>
            <div className="stat" style={{ cursor: 'pointer', outline: origin === '' && !status ? '2px solid var(--brand-500, #10b981)' : undefined }}
                 onClick={() => { setStatus(''); setOrigin(''); setOffset(0); }}
                 title="전체 조회로 필터 초기화">
              <div className="label">전체 누적</div><div className="val">{summary.total.toLocaleString('ko-KR')}</div>
            </div>
            <div className="stat accent-leaf" style={{ cursor: 'pointer', outline: origin === 'op' ? '2px solid var(--brand-500, #10b981)' : undefined }}
                 onClick={() => { setOrigin('op'); setOffset(0); }}
                 title="운영 발신만 조회">
              <div className="label">운영 누적</div>
              <div className="val">{summary.op_total.toLocaleString('ko-KR')}</div>
            </div>
            <div className="stat" style={{ borderLeft: '4px solid #d97706', cursor: 'pointer', outline: origin === 'test' ? '2px solid #d97706' : undefined }}
                 onClick={() => { setOrigin('test'); setOffset(0); }}
                 title="테스트 발신만 조회">
              <div className="label">테스트 누적</div>
              <div className="val" style={{ color: '#854d0e' }}>{summary.test_total.toLocaleString('ko-KR')}</div>
            </div>
            <div className="stat"><div className="label">오늘(한국시간)</div><div className="val">{summary.today_kst.toLocaleString('ko-KR')}</div></div>
            <div className="stat"><div className="label">최근 24시간</div><div className="val">{summary.last_24h.toLocaleString('ko-KR')}</div></div>
          </div>

          <div className="section-label">처리 결과별 (운영 한정 — 테스트 제외) · <span style={{ color: 'var(--text-muted)', fontSize: 11, fontWeight: 400 }}>카드 클릭 시 해당 결과로 필터</span></div>
          <div className="summary" style={{ marginBottom: 14 }}>
            <div className="stat fulfilled" style={{ cursor: 'pointer', outline: status === 'registered' ? '2px solid var(--brand-500, #10b981)' : undefined }}
                 onClick={() => { setStatus('registered'); setOrigin('op'); setOffset(0); }}>
              <div className="label">저장 성공</div>
              <div className="val">{statusN('registered').toLocaleString('ko-KR')}</div>
            </div>
            <div className="stat missed" style={{ cursor: 'pointer', outline: status === 'auth_failed' ? '2px solid #b91c1c' : undefined }}
                 onClick={() => { setStatus('auth_failed'); setOrigin('op'); setOffset(0); }}>
              <div className="label">인증정보 확인 실패</div>
              <div className="val">{statusN('auth_failed').toLocaleString('ko-KR')}</div>
            </div>
            <div className="stat" style={{ borderLeft: '4px solid #ea580c', cursor: 'pointer', outline: status === 'validation_failed' ? '2px solid #ea580c' : undefined }}
                 onClick={() => { setStatus('validation_failed'); setOrigin('op'); setOffset(0); }}>
              <div className="label">수신정보 검증 실패</div>
              <div className="val" style={{ color: '#ea580c' }}>{statusN('validation_failed').toLocaleString('ko-KR')}</div>
            </div>
            <div className="stat" style={{ borderLeft: '4px solid #ea580c', cursor: 'pointer', outline: status === 'invalid_json' ? '2px solid #ea580c' : undefined }}
                 onClick={() => { setStatus('invalid_json'); setOrigin('op'); setOffset(0); }}>
              <div className="label">수신 데이터 형식 오류</div>
              <div className="val" style={{ color: '#ea580c' }}>{statusN('invalid_json').toLocaleString('ko-KR')}</div>
            </div>
            <div className="stat" style={{ borderLeft: '4px solid #7c2d12', cursor: 'pointer', outline: status === 'server_error' ? '2px solid #7c2d12' : undefined }}
                 onClick={() => { setStatus('server_error'); setOrigin('op'); setOffset(0); }}>
              <div className="label">처리 중 오류</div>
              <div className="val" style={{ color: '#7c2d12' }}>{statusN('server_error').toLocaleString('ko-KR')}</div>
            </div>
          </div>
        </>
      )}

      <div className="card filter-card">
        <form onSubmit={onApply} className="filter-form">
          <label className="filter-field">수신일자 시작
            <input type="date" value={from} onChange={e => { setFrom(e.target.value); setOffset(0); }} />
          </label>
          <label className="filter-field">수신일자 종료
            <input type="date" value={to} onChange={e => { setTo(e.target.value); setOffset(0); }} />
          </label>
          <label className="filter-field">결제일자 시작
            <input type="date" value={trxFrom} onChange={e => { setTrxFrom(e.target.value); setOffset(0); }} />
          </label>
          <label className="filter-field">결제일자 종료
            <input type="date" value={trxTo} onChange={e => { setTrxTo(e.target.value); setOffset(0); }} />
          </label>
          <label className="filter-field">처리 결과
            <select value={status} onChange={e => { setStatus(e.target.value as any); setOffset(0); }}>
              <option value="">전체</option>
              <option value="registered">저장 성공</option>
              <option value="cancelled">취소 반영</option>
              <option value="duplicate">중복</option>
              <option value="auth_failed">인증정보 확인 실패</option>
              <option value="invalid_json">수신 데이터 형식 오류</option>
              <option value="validation_failed">수신정보 검증 실패</option>
              <option value="server_error">처리 중 오류</option>
            </select>
          </label>
          <label className="filter-field">발신 구분
            <select value={origin} onChange={e => { setOrigin(e.target.value as any); setOffset(0); }}>
              <option value="op">운영만</option>
              <option value="test">테스트만</option>
              <option value="">전체 (운영+테스트)</option>
            </select>
          </label>
          <label className="filter-field">인증정보 일치 여부
            <select value={tokenMatch} onChange={e => { setTokenMatch(e.target.value as any); setOffset(0); }}>
              <option value="">전체</option>
              <option value="1">일치</option>
              <option value="0">불일치</option>
            </select>
          </label>
          <label className="filter-field" title="기술 필드명: ord_num">주문번호
            <input type="text" value={ordNum} onChange={e => setOrdNum(e.target.value)} style={{ minWidth: 220 }} placeholder="예: 20260821-000123 (정확히 일치)" />
          </label>
          <label className="filter-field" title="기술 필드명: mcht_id">MID / 가맹점 ID
            <input type="text" value={mchtId} onChange={e => setMchtId(e.target.value)} style={{ minWidth: 160 }} placeholder="예: gpay7475dm" />
          </label>
          <label className="filter-field">통합검색 (키워드)
            <input type="text" value={kw} placeholder="raw 본문·에러·IP·User-Agent 등"
                   onChange={e => { setKw(e.target.value); setOffset(0); }} style={{ minWidth: 240 }} />
          </label>
          <label className="filter-field" title="기술 필드명: noti_kind">결제 수신 유형
            <select value={notiKind} onChange={e => { setNotiKind(e.target.value as any); setOffset(0); }}>
              <option value="">전체</option>
              <option value="merchant">일반 결제 수신</option>
              <option value="merchant_discount">할인매매 결제 수신</option>
            </select>
          </label>
          <label className="filter-field" title="기술 필드명: settlmnt_cycle">정산주기
            <input type="text" value={settlmntCycle} onChange={e => { setSettlmntCycle(e.target.value); setOffset(0); }}
                   placeholder="S10/D1/W1/M1" style={{ width: 120 }} />
          </label>
          <button type="submit" className="filter-primary">조회</button>
        </form>

        <details className="more-filters">
          <summary>상세 필터</summary>
          <div className="body">
            <label className="filter-field">HTTP 응답코드
              <input type="number" value={httpStatus} onChange={e => { setHttpStatus(e.target.value); setOffset(0); }} placeholder="200/400/401/500" style={{ width: 140 }} />
            </label>
            <label className="filter-field">수신 데이터 형식
              <select value={parsedOk} onChange={e => { setParsedOk(e.target.value as any); setOffset(0); }}>
                <option value="">전체</option>
                <option value="1">정상</option>
                <option value="0">오류</option>
              </select>
            </label>
            <label className="filter-field">재처리 결과
              <select value={reprocessOutcome} onChange={e => { setReprocessOutcome(e.target.value as any); setOffset(0); }}>
                <option value="">전체</option>
                <option value="registered">저장 성공</option>
                <option value="duplicate">중복</option>
                <option value="auth_failed">인증정보 확인 실패</option>
                <option value="invalid_json">수신 데이터 형식 오류</option>
                <option value="validation_failed">수신정보 검증 실패</option>
                <option value="server_error">처리 중 오류</option>
              </select>
            </label>
            <label className="filter-field" title="기술 필드명: result_cd">결과코드
              <input type="text" value={resultCd} onChange={e => { setResultCd(e.target.value); setOffset(0); }} placeholder="0000/9001/9999" style={{ width: 140 }} />
            </label>
            <label className="filter-field">발신 IP
              <input type="text" value={ip} onChange={e => { setIp(e.target.value); setOffset(0); }} placeholder="정확히 일치" style={{ width: 160 }} />
            </label>
            <label className="filter-field">처리시간 ≥ (ms)
              <input type="number" min={0} value={durationMin} onChange={e => { setDurationMin(e.target.value); setOffset(0); }} placeholder="ms" style={{ width: 120 }} />
            </label>
            <label className="filter-field">처리시간 ≤ (ms)
              <input type="number" min={0} value={durationMax} onChange={e => { setDurationMax(e.target.value); setOffset(0); }} placeholder="ms" style={{ width: 120 }} />
            </label>
            <button type="button" onClick={resetAdvancedFilters} className="filter-ghost">상세 필터 초기화</button>
          </div>
        </details>

        {reprocessMsg && <div className="filter-note">{reprocessMsg}</div>}
        {err && <div className="error">{err}</div>}
      </div>

      {loading ? (
        <SkeletonTable rows={10} cols={6} showHeader />
      ) : (
        <>
          <div className="card pager-card">
            <div className="pager-stats">
              <div>
                <div className="label">조회 결과 총 건수</div>
                <div className="val">{total.toLocaleString('ko-KR')}<span className="unit">건</span></div>
              </div>
              <div>
                <div className="label">현재 페이지</div>
                <div className="val">{curPage}<span className="sep">/</span><span className="denom">{totalPages}</span></div>
              </div>
              <div>
                <div className="label">표시 항목</div>
                <div className="range">{firstIdx.toLocaleString('ko-KR')} ~ {lastIdx.toLocaleString('ko-KR')} 번째</div>
              </div>
            </div>

            <div className="pager-controls">
              <label className="per-page">한 페이지 표시
                <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setOffset(0); }}>
                  <option value={50}>50건</option>
                  <option value={100}>100건</option>
                  <option value={200}>200건</option>
                  <option value={500}>500건</option>
                </select>
              </label>
              <div className="pager-nav">
                <button onClick={() => goPage(1)} disabled={curPage <= 1} className="pager-btn icon" title="처음"><ChevronsLeft size={14} /></button>
                <button onClick={() => goPage(curPage - 1)} disabled={curPage <= 1} className="pager-btn icon" title="이전"><ChevronLeft size={14} /></button>
                <span className="pager-input">
                  <input type="number" min={1} max={totalPages} value={curPage}
                         onChange={e => goPage(Number(e.target.value))} />
                  / <span>{totalPages}</span>
                </span>
                <button onClick={() => goPage(curPage + 1)} disabled={curPage >= totalPages} className="pager-btn icon" title="다음"><ChevronRight size={14} /></button>
                <button onClick={() => goPage(totalPages)} disabled={curPage >= totalPages} className="pager-btn icon" title="마지막"><ChevronsRight size={14} /></button>
              </div>
            </div>
          </div>

          {data.length === 0 ? (
            <div className="card empty-state">
              <Inbox className="empty-icon" size={36} strokeWidth={1.6} />
              <h4>해당 조건의 수신 로그가 없습니다</h4>
              <p>기간이나 필터를 조정해 주세요.</p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0 }}>
              <div className="table-wrap" style={{ maxHeight: '65vh', border: 0 }}>
                <table>
                  <thead><tr>
                    <th className="nowrap">수신시각</th>
                    <th className="nowrap">결제시각</th>
                    <th className="nowrap">결제 수신 유형</th>
                    <th className="nowrap">처리 결과</th>
                    <th className="nowrap">HTTP</th>
                    <th className="nowrap" title="인증정보 일치 여부">인증정보</th>
                    <th className="nowrap" title="수신 데이터 형식(JSON) 파싱 성공 여부">수신 데이터</th>
                    <th className="nowrap">주문번호</th>
                    <th className="nowrap" title="가맹점 고유 ID · 기술 필드명: mcht_id">MID / 가맹점 ID</th>
                    <th className="nowrap">테스트</th>
                    <th className="num nowrap">처리시간</th>
                    <th className="nowrap">발신 IP</th>
                    <th></th>
                  </tr></thead>
                  <tbody>
                    {data.map(r => (
                      <tr key={r.id} className={r.is_test ? 'row-test' : undefined}>
                        <td className="nowrap cell-mono" style={{ color: 'var(--ink)' }}>{r.received_at}</td>
                        <td className="nowrap cell-mono">{r.trx_dttm ?? '-'}</td>
                        <td className="nowrap">
                          <span className={`noti-kind ${r.noti_kind ? NOTI_KIND_CLASS[r.noti_kind] : ''}`}>
                            {r.noti_kind ? NOTI_KIND_KO[r.noti_kind] : '-'}
                            {r.settlmnt_cycle && <span className="cycle">{r.settlmnt_cycle}</span>}
                          </span>
                        </td>
                        <td className={`nowrap log-status ${r.status}`}>
                          {STATUS_KO[r.status] ?? r.status}
                          {r.reprocessed_at && (
                            <span className="reprocess-note">
                              → 재처리: <span className={`log-status ${r.reprocess_outcome ?? ''}`}>
                                {STATUS_KO[r.reprocess_outcome ?? ''] ?? r.reprocess_outcome}
                              </span>
                            </span>
                          )}
                        </td>
                        <td className="nowrap cell-mono" style={{ color: 'var(--ink)' }}>{r.http_status}</td>
                        <td className="nowrap">
                          {r.token_match
                            ? <span style={{ color: 'var(--good)' }}>일치</span>
                            : <span className="cancel-tag">불일치</span>}
                        </td>
                        <td className="nowrap">{r.parsed_ok
                          ? <Check size={15} style={{ color: 'var(--brand-700)' }} />
                          : <X size={15} className="cross-mark" />}</td>
                        <td className="nowrap cell-mono">{r.ord_num ?? '-'}</td>
                        <td className="nowrap cell-mono">{r.mcht_id ?? '-'}</td>
                        <td className="nowrap">{r.is_test ? <span className="tag-test">테스트</span> : '-'}</td>
                        <td className="num nowrap">{r.duration_ms != null ? `${r.duration_ms}ms` : '-'}</td>
                        <td className="nowrap cell-mono">{r.ip ?? '-'}</td>
                        <td>
                          <button onClick={() => setDetail(r)} className="filter-ghost" style={{ padding: '4px 10px', fontSize: 12 }}>상세</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {detail && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="subtitle">노티 수신 상세</div>
                <div className="title">log #{detail.id} · {STATUS_KO[detail.status] ?? detail.status}</div>
              </div>
              <button onClick={() => setDetail(null)} className="modal-close">닫기</button>
            </div>
            <div className="modal-body">
              <div className="kv-grid" style={{ gridTemplateColumns: '140px 1fr', marginBottom: 14 }}>
                <div className="k">수신시각</div><div className="cell-mono" style={{ color: 'var(--ink)' }}>{detail.received_at}</div>
                <div className="k">결제 발생시각</div><div className="cell-mono" style={{ color: 'var(--ink)' }}>{detail.trx_dttm ?? '-'}</div>
                <div className="k">결제 수신 유형</div>
                <div>
                  <span className={`noti-kind ${detail.noti_kind ? NOTI_KIND_CLASS[detail.noti_kind] : ''}`}>
                    {detail.noti_kind ? NOTI_KIND_KO[detail.noti_kind] : '-'}
                  </span>
                  {detail.settlmnt_cycle && <span style={{ marginLeft: 8, color: 'var(--muted)', fontSize: 12, fontWeight: 500 }}>· {detail.settlmnt_cycle}</span>}
                </div>
                <div className="k">발신 IP</div><div className="cell-mono" style={{ color: 'var(--ink)' }}>{detail.ip ?? '-'}</div>
                <div className="k">User-Agent</div><div className="cell-mono" style={{ wordBreak: 'break-all' }}>{detail.user_agent ?? '-'}</div>
                <div className="k">호출 경로</div><div className="cell-mono" style={{ wordBreak: 'break-all' }}>{detail.url_path}</div>
                <div className="k">URL 토큰</div><div className="cell-mono" style={{ wordBreak: 'break-all' }}>{detail.token_path}</div>
                <div className="k">인증정보 일치</div>
                <div style={{ color: detail.token_match ? 'var(--good)' : 'var(--warn)', fontWeight: 600 }}>
                  {detail.token_match ? '일치' : '불일치'}
                </div>
                <div className="k">수신 데이터 형식</div><div>{detail.parsed_ok ? '정상' : '오류'}</div>
                <div className="k">처리 결과</div>
                <div className={`log-status ${detail.status}`}>{STATUS_KO[detail.status] ?? detail.status}</div>
                <div className="k">HTTP 응답</div><div className="cell-mono" style={{ color: 'var(--ink)' }}>{detail.http_status}</div>
                <div className="k">결과 코드</div><div className="cell-mono" style={{ color: 'var(--ink)' }}>{detail.result_cd} ({detail.result_msg})</div>
                <div className="k">처리시간</div><div>{detail.duration_ms != null ? `${detail.duration_ms}ms` : '-'}</div>
                <div className="k">주문번호</div><div className="cell-mono" style={{ color: 'var(--ink)' }}>{detail.ord_num ?? '-'}</div>
                <div className="k">MID / 가맹점 ID</div><div className="cell-mono" style={{ color: 'var(--ink)' }}>{detail.mcht_id ?? '-'}</div>
                <div className="k">테스트 여부</div><div>{detail.is_test ? '테스트' : '운영'}</div>
                {detail.reprocessed_at && (
                  <>
                    <div className="k">재처리 시각</div><div className="cell-mono" style={{ color: 'var(--ink)' }}>{detail.reprocessed_at}</div>
                    <div className="k">재처리 결과</div>
                    <div className={`log-status ${detail.reprocess_outcome ?? ''}`}>
                      {STATUS_KO[detail.reprocess_outcome ?? ''] ?? detail.reprocess_outcome}
                    </div>
                    {detail.reprocess_error && (
                      <>
                        <div className="k">재처리 에러</div>
                        <div className="cell-mono" style={{ color: 'var(--warn)', wordBreak: 'break-all' }}>{detail.reprocess_error}</div>
                      </>
                    )}
                  </>
                )}
              </div>

              {detail.error_detail && (
                <>
                  <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>에러 상세</div>
                  <pre className="code-block error">{(() => {
                    try { return JSON.stringify(JSON.parse(detail.error_detail), null, 2); }
                    catch { return detail.error_detail; }
                  })()}</pre>
                </>
              )}

              <div className="muted" style={{ fontSize: 12, marginBottom: 4 }} title="기술 필드명: raw_body">받은 본문 원본</div>
              <pre className="code-block">{detail.raw_body == null ? '(빈 본문)' : (() => {
                try { return JSON.stringify(JSON.parse(detail.raw_body), null, 2); }
                catch { return detail.raw_body; }
              })()}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
